import { Table } from "../UI/Table";
import { IconFileSpreadsheet, IconHistory } from "@tabler/icons-react";
import { Property } from "../UI/Icons";
import Card from "../UI/Card";
import CardAction from "../UI/CardAction";
import { Selectable } from "../UI/Selectable";
import { Link } from "react-router-dom";
import stylePrelevement from "./Prelevement.module.css";
import { useAllTenantsBalances } from "../../hooks/use-balances";
import useProperties from "../../hooks/use-properties";
import TextInputStandalone from "../UI/TextInputStandalone";
import Text from "../UI/Text";
import { currencyFormatter } from "../../api/Functions";
import { useModal } from "../../hooks/use-modal.jsx";
import FichierPrelevementForm from "./Forms/FichierPrelevementForm";
import HistoriquePrelevementForm from "./Forms/HistoriquePrelevementForm";
import { useEffect, useState } from "react";
import useTableSort from "../../hooks/use-table-sort";

function aplDeduite(tenant) {
  return tenant.caf_deduction && tenant.montant_caf > 0
    ? tenant.montant_caf
    : 0;
}

export default function Prelevement({ tenants }) {
  const { groupedBalances } = useAllTenantsBalances(false, {
    groupBy: { attr: "id_tenant", unique: true },
  });
  const [amountsByTenant, setAmountsByTenant] = useState({});
  const [selectedTenants, setSelectedTenants] = useState([]);

  useEffect(() => {
    const initialValues = tenants.reduce((acc, tenant) => {
      const taux_loyer = tenant.tva ? (tenant.taux_tva || 20) / 100 : 0;
      const taux_charges = tenant.tva_charge
        ? (tenant.taux_tva_charge || tenant.taux_tva || 20) / 100
        : 0;
      const montantAppele =
        tenant.loyer_hc * (1 + taux_loyer) +
        tenant.charges * (1 + taux_charges) +
        (Array.isArray(tenant.autre_appel)
          ? tenant.autre_appel.reduce((sum, c) => {
              const taux_appel = c.tva ? (tenant.taux_tva || 20) / 100 : 0;
              const sign = c.remboursement ? -1 : 1;
              return sum + (sign * Math.abs(c.montant) * (1 + taux_appel) || 0);
            }, 0)
          : 0);
      acc[tenant.id] = Math.max(0, montantAppele - aplDeduite(tenant)).toFixed(
        2,
      );
      return acc;
    }, {});
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setAmountsByTenant(initialValues);
  }, [tenants]);

  const [FichierPrelevement, , openFichierPrelevement] = useModal();
  const [HistoriquePrelevement, , openHistoriquePrelevement] = useModal();

  const { propertiesById } = useProperties({ withById: true });

  const { sort, toggleSort, directionFor } = useTableSort("prelevement_sort");

  const displayedTenants = (() => {
    if (!sort) return tenants;
    const dir = sort.direction === "asc" ? 1 : -1;
    return [...tenants].sort((a, b) => {
      if (sort.key === "solde") {
        return (
          dir *
          ((groupedBalances[a.id]?.balance ?? 0) -
            (groupedBalances[b.id]?.balance ?? 0))
        );
      }
      if (sort.key === "paiement") {
        return dir * ((a.day_payment ?? 0) - (b.day_payment ?? 0));
      }
      return (
        dir *
        (a.nom ?? "").localeCompare(b.nom ?? "", "fr", { sensitivity: "base" })
      );
    });
  })();

  let rows = displayedTenants.map((tenant, i) => {
    const balanceTenant = groupedBalances[tenant.id];
    if (!balanceTenant) return null;

    let showBalanceClass;
    if (balanceTenant.balance >= 0) {
      showBalanceClass = "green";
    } else if (balanceTenant.balance < -tenant.loyer_hc - tenant.charges) {
      showBalanceClass = "error";
    } else {
      showBalanceClass = "warning";
    }
    let property = propertiesById[tenant.id_bien];

    return (
      <Table.Row key={i}>
        <Table.Cell className={"flex align-items-center gap10"}>
          <Selectable.Checkbox value={tenant.id} index={i} />
          <div className={"marginL5"}>
            <Link to={`/bailleur/tenants/${tenant.id}`}>{tenant.nom}</Link>
            <div className={stylePrelevement.smallText}>
              <Link
                key={property?.id}
                to={"/bailleur/properties/" + property?.id}
              >
                <Property /> {property?.nom}
              </Link>
            </div>
          </div>
        </Table.Cell>
        <Table.Cell>
          <div className={`marginL10 text-right ${showBalanceClass}`}>
            {balanceTenant.balance.toLocaleString()}€
          </div>
        </Table.Cell>
        <Table.Cell>Le {tenant.day_payment} du mois</Table.Cell>
        <Table.Cell>
          <div className={stylePrelevement.amountCell}>
            <TextInputStandalone
              type={"number"}
              unit={"€"}
              value={amountsByTenant[tenant.id] ?? ""}
              onChange={(e) => {
                setAmountsByTenant((prev) => ({
                  ...prev,
                  [tenant.id]: e.target.value,
                }));
              }}
            />
            {aplDeduite(tenant) > 0 && (
              <Text variant="deci">
                APL déduite : {currencyFormatter(aplDeduite(tenant))}
              </Text>
            )}
          </div>
        </Table.Cell>
      </Table.Row>
    );
  });

  return (
    <>
      <FichierPrelevement size={"medium"}>
        <FichierPrelevementForm
          selectedTenants={selectedTenants}
          amountsByTenant={amountsByTenant}
        />
      </FichierPrelevement>
      <HistoriquePrelevement size={"large"}>
        <HistoriquePrelevementForm />
      </HistoriquePrelevement>
      <Selectable items={tenants}>
        <div className={"marginB20 flex gap10 wrap"}>
          <div className={stylePrelevement.buttonCard}>
            <Selectable.CardActionButton
              icon={IconFileSpreadsheet}
              description={
                "Permet d’éditer le fichier de prélèvement à transmettre à votre banque afin de prélever le loyer de vos locataires qui ont signé un mandat de prélèvement."
              }
              title={"Éditer le fichier de prélèvement"}
              onClick={async (selectedItems) => {
                openFichierPrelevement();
                setSelectedTenants(
                  tenants.filter((tenant) => selectedItems.includes(tenant.id)),
                );
              }}
            />
          </div>
          <div className={stylePrelevement.buttonCard}>
            <CardAction
              icon={IconHistory}
              description={
                "Retrouvez les fichiers de prélèvements déjà édités ainsi que les bordereaux associés reprenant l'ensemble des informations."
              }
              title={"Consulter l'historique des prélèvements"}
              onClick={() => openHistoriquePrelevement()}
            />
          </div>
        </div>
        <Card>
          <Table variant="card">
            <Table.Header>
              <Table.Column sortDirection={directionFor("nom")}>
                <div className={stylePrelevement.selectAllHeader}>
                  <label className={stylePrelevement.checkboxLabel}>
                    <Selectable.Checkbox selectAll />
                    <span className="srOnly">Tout sélectionner</span>
                  </label>
                  <Table.SortButton
                    direction={directionFor("nom")}
                    onSort={() => toggleSort("nom")}
                  >
                    Locataire
                  </Table.SortButton>
                </div>
              </Table.Column>
              <Table.Column
                align={"right"}
                sortDirection={directionFor("solde")}
                onSort={() => toggleSort("solde")}
              >
                Solde
              </Table.Column>
              <Table.Column
                align={"left"}
                sortDirection={directionFor("paiement")}
                onSort={() => toggleSort("paiement")}
              >
                Paiement
              </Table.Column>
              <Table.Column align={"left"}>Montant à prélever</Table.Column>
            </Table.Header>
            <Table.Body>
              {rows.length > 0 ? (
                rows
              ) : (
                <Table.Row>
                  <Table.Cell
                    colSpan="100%"
                    className={"text-center secondaryText paddingT20"}
                  >
                    Aucun locataire
                  </Table.Cell>
                </Table.Row>
              )}
            </Table.Body>
          </Table>
        </Card>
      </Selectable>
    </>
  );
}
